export interface CmpConfig {
  policyVersion: number;
  bannerVersion: number;
  defaultLanguage: string;
  themeJson?: ThemeConfig;
  translations: TranslationConfig[];
  categories: CategoryConfig[];
  granularCookieMode?: boolean;
  cookies?: CookieConfig[];
  /** From API variant: KVKK | GDPR | CCPA | Global */
  complianceMode?: string;
}

export interface ThemeConfig {
  primaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  buttonStyle?: "rounded" | "square";
  position?: "bottom" | "top" | "center" | "bottom-left";
  /** Admin theme JSON: login sonrası tek metin (tüm diller). */
  loginReconsentNotice?: string;
  /** Admin theme JSON: dil kodu → metin (örn. { "tr": "...", "en": "..." }). */
  loginReconsentNoticeJson?: Record<string, string>;
  /** Banner'ın sağ altında EntaPro logosunu göster (varsayılan: true). */
  logoVisible?: boolean;
  /** Tenant'a özel CSS — varsayılan banner stillerinden SONRA enjekte edilir, onları ezer. */
  customCss?: string;
}

export interface TranslationConfig {
  id: string;
  language: string;
  bannerTitle: string;
  bannerDescription: string;
  buttonsJson?: {
    acceptAll?: string;
    rejectAll?: string;
    managePreferences?: string;
    save?: string;
  };
}

export interface CategoryConfig {
  id: string;
  key: string;
  required: boolean;
  defaultEnabled: boolean;
  sortOrder: number;
  nameJson?: Record<string, string>;
  descJson?: Record<string, string>;
}

export interface CookieConfig {
  key: string;
  cookieName: string;
  cookieDomain: string;
  category: string;
  vendorName?: string;
  purposeJson?: Record<string, string>;
}

let cachedConfig: CmpConfig | null = null;

/**
 * Parse a field that might be a JSON string or already an object.
 * Handles double-encoded JSON from the API (e.g. "{\"key\":\"value\"}").
 */
function parseJsonField<T>(value: unknown): T | undefined {
  if (!value) return undefined;
  if (typeof value === "string") {
    try { return JSON.parse(value) as T; } catch { return undefined; }
  }
  return value as T;
}

/**
 * Normalize config data - parse any JSON string fields into objects.
 */
function normalizeConfig(raw: CmpConfig): CmpConfig {
  return {
    ...raw,
    themeJson: parseJsonField<ThemeConfig>(raw.themeJson) || raw.themeJson,
    translations: raw.translations.map((t) => ({
      ...t,
      buttonsJson: parseJsonField<TranslationConfig["buttonsJson"]>(t.buttonsJson),
    })),
    categories: raw.categories.map((c) => ({
      ...c,
      nameJson: parseJsonField<Record<string, string>>(c.nameJson),
      descJson: parseJsonField<Record<string, string>>(c.descJson),
    })),
    cookies: (raw.cookies || []).map((cookie) => ({
      ...cookie,
      category: (cookie.category || "unclassified").toLowerCase(),
      purposeJson: parseJsonField<Record<string, string>>(cookie.purposeJson),
    })),
  };
}

/**
 * Tarayıcı dilini "tr" veya "en" olarak normalize et.
 * navigator.language = "tr", "tr-TR", "tr-CY" → "tr"
 * Diğer her şey → "en"
 */
export function detectUserLang(): "tr" | "en" {
  const lang = (navigator.language || "en").toLowerCase();
  return lang.startsWith("tr") ? "tr" : "en";
}

let detectedLang: "tr" | "en" | null = null;

export function getUserLang(): "tr" | "en" {
  if (!detectedLang) detectedLang = detectUserLang();
  return detectedLang;
}

export async function fetchConfig(apiBase?: string, lang?: string, hostnameOverride?: string): Promise<CmpConfig> {
  if (cachedConfig) return cachedConfig;

  const hostname = hostnameOverride || window.location.hostname;
  const base = apiBase || "";
  const effectiveLang = lang || getUserLang();
  const res = await fetch(
    `${base}/cmp/config?hostname=${encodeURIComponent(hostname)}&lang=${effectiveLang}`
  );

  if (!res.ok) {
    throw new Error(`CMP: Failed to fetch config for ${hostname} (${res.status})`);
  }

  const raw: CmpConfig = await res.json();
  // Backend'den gelen defaultLanguage'i kullanıcı diliyle override et
  cachedConfig = normalizeConfig({ ...raw, defaultLanguage: effectiveLang });
  return cachedConfig;
}

export function getCachedConfig(): CmpConfig | null {
  return cachedConfig;
}

export function clearConfigCache(): void {
  cachedConfig = null;
}
